import {
  ECapabilityKey,
  EPermissionType,
  TCapabilities,
  TNotificationKey,
  PRIMARY_CONTACT_KEY,
  TUserPermissionsUI,
  TCustomSharedPermissionSetUI,
} from 'data/types/permissions.types';
import { ENotificationTypesDB, ENotificationTypesUI, TAnimalUpdateConfig } from 'data/types/property.types';

export enum EPermissionGroup {
  ANIMALS = 'Animals',
  APPLICANTS_AND_RESIDENTS = 'Applicants and Residents',
  PROPERTIES = 'Properties',
  REPORTING = 'Reporting',
  TEAM_MEMBERS = 'Team Members',
  COMPLAINTS = 'Complaints',
  SETTINGS = 'Settings',
  POLICIES = 'Policies',
}

export type TPermissionConfig = {
  key: ECapabilityKey;
  label: string;
  group: EPermissionGroup;
  defaultValue: boolean;
  info?: string;
  note?: string;
  noteAppliesTo?: EPermissionType[];
};

export const PERMISSION_GROUPS = [
  EPermissionGroup.ANIMALS,
  EPermissionGroup.APPLICANTS_AND_RESIDENTS,
  EPermissionGroup.PROPERTIES,
  EPermissionGroup.REPORTING,
  EPermissionGroup.TEAM_MEMBERS,
  EPermissionGroup.COMPLAINTS,
  EPermissionGroup.SETTINGS,
] as const;

export const PERMISSIONS_CONFIG: TPermissionConfig[] = [
  {
    key: ECapabilityKey.APPROVE_AND_DENY_ANIMALS,
    label: 'Approve & Deny Animals',
    group: EPermissionGroup.ANIMALS,
    defaultValue: true,
    note: 'excludes ESAs and service animals',
    noteAppliesTo: [EPermissionType.CUSTOM_INDIVIDUAL, EPermissionType.CUSTOM_SHARED],
  },
  {
    key: ECapabilityKey.ADD_EDIT_ANIMALS,
    label: 'Create & Edit Animals',
    group: EPermissionGroup.ANIMALS,
    defaultValue: true,
  },
  {
    key: ECapabilityKey.RETRACT_AND_REAPPLY_ANIMALS,
    label: 'Retract & Re-Apply Animals',
    group: EPermissionGroup.ANIMALS,
    defaultValue: true,
  },
  {
    key: ECapabilityKey.IMPORT_RESIDENTS,
    label: 'Import Residents File',
    group: EPermissionGroup.APPLICANTS_AND_RESIDENTS,
    defaultValue: false,
    info: 'Users can upload a CSV file of residents.',
  },
  {
    key: ECapabilityKey.RESIDENCY_EDITING,
    label: 'Residency Editing',
    group: EPermissionGroup.APPLICANTS_AND_RESIDENTS,
    defaultValue: true,
    info: 'Users can edit the property, unit number, and lease dates of existing residents and applicants. Users can also approve or retract applicant residencies and move residents in or out.',
  },
  {
    key: ECapabilityKey.CREATE_AND_EDIT_PROPERTIES,
    label: 'Create & Edit Properties',
    group: EPermissionGroup.PROPERTIES,
    defaultValue: true,
  },
  {
    key: ECapabilityKey.DELETE_PROPERTIES,
    label: 'Delete Properties',
    group: EPermissionGroup.PROPERTIES,
    defaultValue: false,
  },
  {
    key: ECapabilityKey.CREATE_AND_EDIT_ACCESS_CODE,
    label: 'Create & Edit Access Codes',
    group: EPermissionGroup.PROPERTIES,
    defaultValue: false,
    info: 'Users can create access codes for properties they have access to, and edit or delete access codes they personally created.',
  },
  {
    key: ECapabilityKey.ANIMAL_REQUIREMENTS_AND_RESTRICTIONS,
    label: 'Animal Requirements & Restrictions Access',
    group: EPermissionGroup.PROPERTIES,
    defaultValue: true,
    info: 'Users can create new and edit existing sets of animal requirements and restrictions. They can also assign properties to these sets and delete sets that they created.',
  },
  {
    key: ECapabilityKey.AUTOMATED_REPORTING_ACCESS,
    label: 'Automated Reporting Access',
    group: EPermissionGroup.REPORTING,
    defaultValue: true,
    note: 'only applicable for plans that offer reporting',
  },
  {
    key: ECapabilityKey.TEAM_MEMBERS_ACCESS,
    label: 'Team Member Access',
    group: EPermissionGroup.TEAM_MEMBERS,
    defaultValue: true,
    info: "Users can invite team members with the same access level but cannot edit other users' permissions. They can assign team members to any properties they have access to.",
  },
  {
    key: ECapabilityKey.DELETE_TEAM_MEMBERS,
    label: 'Delete Team Members',
    group: EPermissionGroup.TEAM_MEMBERS,
    defaultValue: false,
  },
  {
    key: ECapabilityKey.COMPLAINTS_MANAGEMENT,
    label: 'Complaints Management',
    group: EPermissionGroup.COMPLAINTS,
    defaultValue: true,
  },
  {
    key: ECapabilityKey.ACCESS_TO_BILLING_METHOD,
    label: 'Access to Billing Method',
    group: EPermissionGroup.SETTINGS,
    defaultValue: false,
    info: 'Users can edit the billing method on file when the account is configured to be invoiced directly through the platform.',
  },
];

export const getDefaultPermissions = (): TCapabilities =>
  PERMISSIONS_CONFIG.reduce((acc, item) => {
    acc[item.key] = item.defaultValue;
    return acc;
  }, {} as TCapabilities);

export const getPermissionsByGroup = (): Record<EPermissionGroup, TPermissionConfig[]> =>
  PERMISSIONS_CONFIG.reduce((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = [];
    }
    acc[item.group].push(item);
    return acc;
  }, {} as Record<EPermissionGroup, TPermissionConfig[]>);

export type TNotificationConfig = {
  key: TNotificationKey;
  label: string;
};

export const NOTIFICATION_CONFIG: TNotificationConfig[] = [
  { key: ENotificationTypesDB.ANIMAL_APPROVALS_AND_DENIALS, label: ENotificationTypesUI.ANIMAL_APPROVALS_AND_DENIALS },
  {
    key: ENotificationTypesDB.RESIDENT_DOES_NOT_HAVE_ANIMALS,
    label: ENotificationTypesUI.RESIDENT_DOES_NOT_HAVE_ANIMALS,
  },
  { key: PRIMARY_CONTACT_KEY, label: 'Primary Contact' },
  { key: ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY, label: ENotificationTypesUI.ANIMAL_ACTIVITY_SUMMARY },
];

export const NOTIFICATION_DEFAULTS: Partial<Record<EPermissionType, TNotificationKey[]>> = {
  [EPermissionType.FULL]: [ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY],
  [EPermissionType.CUSTOM_INDIVIDUAL]: [
    ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY,
    ENotificationTypesDB.ANIMAL_APPROVALS_AND_DENIALS,
  ],
  [EPermissionType.READ]: [ENotificationTypesDB.ANIMAL_APPROVALS_AND_DENIALS],
};

export const getDefaultNotifications = (level: EPermissionType): TNotificationKey[] =>
  NOTIFICATION_DEFAULTS[level] || [];

/**
 * Get display name for a permission type.
 * For fixed types returns label from PERMISSION_TYPE_LABELS.
 * For CUSTOM_SHARED returns the set name if provided, otherwise empty string.
 */
export const getPermissionTypeDisplay = (type: EPermissionType, setName?: string): string => {
  // --- Permission type display labels ---
  const PERMISSION_TYPE_LABELS: Partial<Record<EPermissionType, string>> = {
    [EPermissionType.FULL]: 'Full Access',
    [EPermissionType.READ]: 'Read-Only',
    [EPermissionType.CUSTOM_INDIVIDUAL]: 'Custom',
  };
  return PERMISSION_TYPE_LABELS[type] || setName || '';
};

// --- Hardcoded restrictions (view mode only, not configurable) ---

export type THardcodedRestriction = {
  label: string;
  group: EPermissionGroup;
  values: Partial<Record<EPermissionType, boolean>>;
};

export const HARDCODED_RESTRICTIONS: THardcodedRestriction[] = [
  {
    label: 'Create, Edit, or Delete Policies',
    group: EPermissionGroup.POLICIES,
    values: {
      [EPermissionType.FULL]: true,
      [EPermissionType.READ]: false,
      [EPermissionType.CUSTOM_INDIVIDUAL]: false,
      [EPermissionType.CUSTOM_SHARED]: false,
    },
  },
];

// --- Permission type-based restrictions ---
// What a user can/cannot do based on their permission type (not capability toggles).
// Used by usePmPermissions.canByType() for centralized type-level access checks.

export enum EPermissionTypeRestriction {
  APPROVE_ESA_SA = 'APPROVE_ESA_SA',
  RETRACT_REAPPLY_ESA_SA = 'RETRACT_REAPPLY_ESA_SA',
  EDIT_POLICIES = 'EDIT_POLICIES',
}

export const PERMISSION_TYPE_RESTRICTIONS: Record<EPermissionTypeRestriction, EPermissionType[]> = {
  [EPermissionTypeRestriction.APPROVE_ESA_SA]: [EPermissionType.FULL],
  // PP-10214: retract and re-apply move an application's status, so they carry the same ESA/SA
  // restriction as approve/deny. Separate from APPROVE_ESA_SA because the two are distinct actions
  // that happen to share a rule today — collapsing them would hide it if either one ever changes.
  [EPermissionTypeRestriction.RETRACT_REAPPLY_ESA_SA]: [EPermissionType.FULL],
  [EPermissionTypeRestriction.EDIT_POLICIES]: [EPermissionType.FULL],
};

/** Derives a stable key from TUserPermissionsUI — matches PermissionTypeRadioGroup's selectedKey logic */
export const getPermissionKey = (permissions: TUserPermissionsUI): string =>
  permissions.type === EPermissionType.CUSTOM_SHARED ? permissions.setId : permissions.type;

export const areCapabilitiesEqual = (a: TCapabilities, b: TCapabilities): boolean =>
  Object.values(ECapabilityKey).every((key) => a[key] === b[key]);

export const arePermissionsEqual = (a: TUserPermissionsUI, b: TUserPermissionsUI): boolean => {
  if (a.type !== b.type) {
    return false;
  }
  if (a.type === EPermissionType.CUSTOM_SHARED && b.type === EPermissionType.CUSTOM_SHARED) {
    return a.setId === b.setId;
  }
  if (a.type === EPermissionType.CUSTOM_INDIVIDUAL && b.type === EPermissionType.CUSTOM_INDIVIDUAL) {
    return areCapabilitiesEqual(a.capabilities, b.capabilities);
  }
  return true;
};

export const areNotificationsEqual = (a: TNotificationKey[], b: TNotificationKey[]): boolean =>
  a.length === b.length && a.every((key) => b.includes(key));

// Compares the Animal Activity Summary detail config (schedule + included sections) — all flat primitives
export const isAnimalUpdateConfigEqual = (a: TAnimalUpdateConfig, b: TAnimalUpdateConfig): boolean =>
  (Object.keys(a) as (keyof TAnimalUpdateConfig)[]).every((key) => a[key] === b[key]);

/** Builds filter values for permission type select filters on TM tables */
export const getPermissionFilterValues = (sets?: TCustomSharedPermissionSetUI[]) => [
  { label: 'All', value: '' },
  { label: getPermissionTypeDisplay(EPermissionType.FULL), value: EPermissionType.FULL },
  { label: getPermissionTypeDisplay(EPermissionType.READ), value: EPermissionType.READ },
  { label: getPermissionTypeDisplay(EPermissionType.CUSTOM_INDIVIDUAL), value: EPermissionType.CUSTOM_INDIVIDUAL },
  ...(sets || []).map((set) => ({
    label: set.name,
    value: `${EPermissionType.CUSTOM_SHARED}:${set.id}`,
  })),
];

/** Splits the CUSTOM_SHARED:<id> filter value built above into separate permissionType & permissionsId params for BE */
export const normalizePermissionFilterSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  const permType = params.get('permissionType');
  if (permType?.startsWith(`${EPermissionType.CUSTOM_SHARED}:`)) {
    params.set('permissionType', EPermissionType.CUSTOM_SHARED);
    params.set('permissionsId', permType.split(':')[1]);
  }
  return params.toString();
};
